import jsPDF from 'jspdf';
import { ReceiptData, PDFConfig } from './types';

const RULE_GRAY: [number, number, number] = [200, 200, 200];
const STAMP_RED: [number, number, number] = [220, 38, 38];

/**
 * Draw a stamp-style badge with bold uppercase text inside a red
 * rounded outline. Replaces the previous PNG-based stamps so the
 * text is always crisp and correctly cased.
 */
const drawStamp = (
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number
) => {
  pdf.saveGraphicsState?.();
  pdf.setDrawColor(...STAMP_RED);
  pdf.setLineWidth(2.5);
  pdf.roundedRect(x, y, width, height, 4, 4, 'S');

  pdf.setTextColor(...STAMP_RED);
  // Use courier-bold: helvetica-bold in jsPDF has a rendering bug
  // where capital "I" overlaps the next glyph in some PDF viewers.
  pdf.setFont('courier', 'bold');
  let fontSize = 26;
  pdf.setFontSize(fontSize);
  while (pdf.getTextWidth(text) > width * 0.78 && fontSize > 6) {
    fontSize -= 1;
    pdf.setFontSize(fontSize);
  }
  const textYmm = y + height / 2 + (fontSize * 0.3528) / 3;
  pdf.text(text, x + width / 2, textYmm, { align: 'center' });
  pdf.restoreGraphicsState?.();
};

const setTextGray = (pdf: jsPDF) => {
  pdf.setTextColor(120, 120, 120);
};
const setTextDark = (pdf: jsPDF) => {
  pdf.setTextColor(0, 0, 0);
};
const setTextBody = (pdf: jsPDF) => {
  pdf.setTextColor(51, 51, 51);
};

const labelsFor = (type: ReceiptData['documentType']) => {
  switch (type) {
    case 'invoice':
      return { title: 'INVOICE', dateLabel: 'Invoice Date' };
    case 'estimate':
      return { title: 'ESTIMATE', dateLabel: 'Estimate Date' };
    case 'receipt':
    default:
      return { title: 'RECEIPT', dateLabel: 'Receipt Date' };
  }
};

export const addHeader = (pdf: jsPDF, receiptData: ReceiptData, config: PDFConfig): number => {
  const businessProfile = receiptData.businessProfile || {};
  const businessName =
    businessProfile.business_name || businessProfile.businessName || 'Business';
  const { title, dateLabel } = labelsFor(receiptData.documentType);

  // Optional centered company logo at the very top — pushes the rest down.
  let topY = 22;
  if (receiptData.logoDataUri) {
    const logoH = 26;
    const logoW = 26;
    const logoX = (config.pageWidth - logoW) / 2;
    const logoY = 12;
    try {
      pdf.addImage(receiptData.logoDataUri, logoX, logoY, logoW, logoH, undefined, 'FAST');
    } catch (e) {
      console.warn('Failed to draw logo on PDF:', e);
    }
    topY = logoY + logoH + 12;
  }

  const rightX = config.pageWidth - config.margin;

  // RIGHT: Business name + address + contact
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  setTextDark(pdf);
  pdf.text(businessName, rightX, topY, { align: 'right' });

  let rightY = topY + 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  setTextBody(pdf);

  const addressLines: string[] = [];
  if (businessProfile.address) addressLines.push(String(businessProfile.address));
  if (businessProfile.city) addressLines.push(String(businessProfile.city));
  if (businessProfile.postcode) addressLines.push(String(businessProfile.postcode));
  // Country intentionally omitted from the printed address.

  const contactEmail =
    businessProfile.business_email || businessProfile.email || businessProfile.contact_email;
  const contactPhone =
    businessProfile.phone_number || businessProfile.phoneNumber || businessProfile.phone;

  const contactLines: string[] = [];
  if (contactPhone) contactLines.push(String(contactPhone));
  if (contactEmail) contactLines.push(String(contactEmail));
  if (businessProfile.website) contactLines.push(String(businessProfile.website));

  // VAT / Tax ID — shown when toggled on in Payment Settings.
  const rs =
    receiptData.receiptSettings ||
    businessProfile.receipt_settings ||
    businessProfile.receiptSettings;
  if (rs?.includeTaxId && rs?.taxId) {
    contactLines.push(`VAT: ${String(rs.taxId)}`);
  }

  const LINE_GAP = 5;
  addressLines.forEach(line => {
    pdf.text(line, rightX, rightY, { align: 'right' });
    rightY += LINE_GAP;
  });
  contactLines.forEach(line => {
    pdf.text(line, rightX, rightY, { align: 'right' });
    rightY += LINE_GAP;
  });

  // LEFT: Document title
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(22);
  setTextDark(pdf);
  pdf.text(title, config.margin, topY);

  // Status stamp under the document title
  const docTypeForStamp = receiptData.documentType ?? 'receipt';
  const stampLabel =
    docTypeForStamp === 'receipt' ? 'PAID' :
    docTypeForStamp === 'invoice' ? 'UNPAID' :
    docTypeForStamp === 'estimate' ? 'ESTIMATE' :
    null;
  if (stampLabel) {
    const stampW = 55;
    const stampH = 22;
    drawStamp(pdf, stampLabel, config.margin, topY + 8, stampW, stampH);
  }

  // RIGHT (below business contact): metadata
  let metaY = Math.max(rightY + 8, topY + 30);
  const metaLabelX = rightX - 70;
  const metaValueX = rightX;

  const writeMetaRow = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    setTextDark(pdf);
    pdf.text(label, metaLabelX, metaY);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    setTextBody(pdf);
    pdf.text(value, metaValueX, metaY, { align: 'right' });
    metaY += 6;
  };

  writeMetaRow(dateLabel, receiptData.date);

  return metaY + 8;
};

// Metadata moved into header — keep as no-op for back-compat
export const addReceiptDetails = (
  _pdf: jsPDF,
  _receiptData: ReceiptData,
  _config: PDFConfig,
  yPosition: number
): number => {
  return yPosition;
};

export const addDescription = (
  pdf: jsPDF,
  receiptData: ReceiptData,
  config: PDFConfig,
  yPosition: number
): number => {
  const innerWidth = config.pageWidth - 2 * config.margin;
  const amountColX = config.pageWidth - config.margin;
  const descMaxWidth = innerWidth - 50;

  // Top rule
  pdf.setDrawColor(...RULE_GRAY);
  pdf.setLineWidth(0.3);
  pdf.line(config.margin, yPosition, config.pageWidth - config.margin, yPosition);
  yPosition += 6;

  // Column headers
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  setTextDark(pdf);
  pdf.text('Description', config.margin, yPosition);
  pdf.text(`Amount ${receiptData.currency}`, amountColX, yPosition, { align: 'right' });
  yPosition += 4;

  // Header underline
  pdf.line(config.margin, yPosition, config.pageWidth - config.margin, yPosition);
  yPosition += 7;

  // Line item — render line-by-line with page-break handling so long
  // descriptions flow across multiple pages instead of being clipped.
  const description = receiptData.description || 'Payment received';
  const descLines: string[] = pdf.splitTextToSize(description, descMaxWidth);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  setTextBody(pdf);

  const bottomLimit = config.pageHeight - 30; // leave room for footer
  pdf.text(receiptData.amount.toFixed(2), amountColX, yPosition, { align: 'right' });

  for (let i = 0; i < descLines.length; i++) {
    if (yPosition > bottomLimit) {
      pdf.addPage();
      yPosition = config.margin + 5;
      // Repeat column headers on the new page for clarity
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      setTextDark(pdf);
      pdf.text('Description (cont.)', config.margin, yPosition);
      yPosition += 4;
      pdf.setDrawColor(...RULE_GRAY);
      pdf.line(config.margin, yPosition, config.pageWidth - config.margin, yPosition);
      yPosition += 7;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      setTextBody(pdf);
    }
    pdf.text(descLines[i], config.margin, yPosition);
    yPosition += config.lineHeight;
  }
  yPosition += 8;

  // Ensure the totals block fits on the current page; otherwise start a new one.
  if (yPosition > config.pageHeight - 60) {
    pdf.addPage();
    yPosition = config.margin + 5;
  }

  // Totals area — right-aligned label/value pairs
  const totalsLabelX = config.pageWidth - config.margin - 45;
  const totalsValueX = amountColX;

  // Thin rule above subtotal
  pdf.setDrawColor(...RULE_GRAY);
  pdf.line(totalsLabelX - 5, yPosition - 4, config.pageWidth - config.margin, yPosition - 4);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  setTextBody(pdf);
  pdf.text('Subtotal', totalsLabelX, yPosition);
  pdf.text(receiptData.amount.toFixed(2), totalsValueX, yPosition, { align: 'right' });
  yPosition += 6;

  pdf.text('TOTAL NO VAT', totalsLabelX, yPosition);
  pdf.text('0.00', totalsValueX, yPosition, { align: 'right' });
  yPosition += 4;

  // Rule above grand total
  pdf.line(totalsLabelX - 5, yPosition, config.pageWidth - config.margin, yPosition);
  yPosition += 6;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  setTextDark(pdf);
  pdf.text(`TOTAL ${receiptData.currency}`, totalsLabelX, yPosition);
  pdf.text(receiptData.amount.toFixed(2), totalsValueX, yPosition, { align: 'right' });
  yPosition += 14;

  return yPosition;
};

export const addCustomerDetails = (
  pdf: jsPDF,
  receiptData: ReceiptData,
  config: PDFConfig,
  yPosition: number
): number => {
  // Status line — varies by document type
  const docType = receiptData.documentType ?? 'receipt';
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  setTextDark(pdf);

  if (docType === 'receipt') {
    pdf.text(`Paid: ${receiptData.date}`, config.margin, yPosition);
    yPosition += 6;
    pdf.setFont('helvetica', 'normal');
    setTextBody(pdf);
    pdf.text(`Payment Method: ${receiptData.paymentMethod || 'Bank Transfer'}`, config.margin, yPosition);
    yPosition += 10;
  } else if (docType === 'invoice') {
    const dueText = receiptData.dueDate ? `Due: ${receiptData.dueDate}` : 'Due on receipt';
    pdf.text(dueText, config.margin, yPosition);
    yPosition += 10;
  } else if (docType === 'estimate') {
    pdf.text('Estimate — not a request for payment', config.margin, yPosition);
    yPosition += 10;
  }

  const hasContact =
    receiptData.customerName ||
    receiptData.customerEmail ||
    receiptData.customerPhone ||
    (receiptData.includeAddress &&
      receiptData.customerAddress &&
      (receiptData.customerAddress.street ||
        receiptData.customerAddress.city ||
        receiptData.customerAddress.postcode ||
        receiptData.customerAddress.country));

  if (!hasContact) return yPosition;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  setTextDark(pdf);
  pdf.text('Billed To', config.margin, yPosition);
  yPosition += 5;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  setTextBody(pdf);

  if (receiptData.customerName) {
    pdf.text(receiptData.customerName, config.margin, yPosition);
    yPosition += 5;
  }
  if (receiptData.includeAddress && receiptData.customerAddress) {
    const a = receiptData.customerAddress;
    if (a.street) {
      pdf.text(a.street, config.margin, yPosition);
      yPosition += 5;
    }
    if (a.city) {
      pdf.text(a.city, config.margin, yPosition);
      yPosition += 5;
    }
    if (a.postcode) {
      pdf.text(a.postcode, config.margin, yPosition);
      yPosition += 5;
    }
    // Country intentionally omitted from the printed address.
  }
  if (receiptData.customerPhone) {
    pdf.text(receiptData.customerPhone, config.margin, yPosition);
    yPosition += 5;
  }
  if (receiptData.customerEmail) {
    pdf.text(receiptData.customerEmail, config.margin, yPosition);
    yPosition += 5;
  }

  yPosition += 6;
  return yPosition;
};

export const addBankDetails = (
  pdf: jsPDF,
  receiptData: ReceiptData,
  config: PDFConfig,
  yPosition: number
): number => {
  if (!receiptData.bankDetails) return yPosition;

  // Only payment requests carry bank details — receipts (already paid) and
  // estimates (not a request for payment) intentionally omit them.
  const docType = receiptData.documentType ?? 'receipt';
  if (docType !== 'invoice') return yPosition;

  const fields = [
    { label: 'Account Name', value: receiptData.bankDetails.accountName },
    { label: 'Bank', value: receiptData.bankDetails.bankName },
    { label: 'Sort Code', value: receiptData.bankDetails.sortCode },
    { label: 'Account Number', value: receiptData.bankDetails.accountNumber },
    { label: 'IBAN', value: receiptData.bankDetails.iban },
    { label: 'SWIFT/BIC', value: receiptData.bankDetails.swift }
  ].filter(f => f.value);

  if (fields.length === 0) return yPosition;

  // Align with the "Billed To" header on the left (which is rendered
  // after a 10mm status line offset in addCustomerDetails for invoices).
  yPosition += 10;

  const rightEdge = config.pageWidth - config.margin;
  const valueX = rightEdge;
  const labelX = rightEdge - 70;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  setTextDark(pdf);
  pdf.text('Bank Details for Payment', labelX, yPosition);
  yPosition += 5;

  fields.forEach(f => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    setTextGray(pdf);
    pdf.text(f.label, labelX, yPosition);
    setTextBody(pdf);
    pdf.text(String(f.value), valueX, yPosition, { align: 'right' });
    yPosition += 5.5;
  });

  return yPosition + 6;
};

// Business details fully collapsed into the header — no-op
export const addBusinessDetails = (
  _pdf: jsPDF,
  _receiptData: ReceiptData,
  _config: PDFConfig,
  yPosition: number
): number => {
  return yPosition;
};

export const addFooter = (pdf: jsPDF, config: PDFConfig, receiptData?: ReceiptData): void => {
  const yPosition = config.pageHeight - 18;

  // Thin top rule
  pdf.setDrawColor(...RULE_GRAY);
  pdf.setLineWidth(0.3);
  pdf.line(config.margin, yPosition - 6, config.pageWidth - config.margin, yPosition - 6);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  setTextGray(pdf);

  const bp = receiptData?.businessProfile || {};
  const businessName = bp.business_name || bp.businessName || '';
  const address = bp.address || '';
  const officeLine = [businessName && `Registered Office: ${businessName}`, address]
    .filter(Boolean)
    .join(', ');

  if (officeLine) {
    pdf.text(officeLine, config.pageWidth / 2, yPosition, { align: 'center' });
  }
};
